import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../services/api';
import { Wifi, CheckCircle } from 'lucide-react';
import { latinAmericaCountries, validatePhoneNumber } from '../data/countries';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    country_code: 'MX',
    password: '',
    confirmPassword: '',
    router_count: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  // Check maintenance mode on load
  React.useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/status-public`);
        const data = await response.json();
        setMaintenanceStatus(data);
        
        // Si el registro está bloqueado, mostrar modal
        if (data.registration_disabled) {
          setShowMaintenanceModal(true);
        }
      } catch (err) {
        console.error('Error checking maintenance:', err);
      }
    };
    checkMaintenance();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Bloquear submit si el registro está deshabilitado
    if (maintenanceStatus?.registration_disabled) {
      setShowMaintenanceModal(true);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    // Validar teléfono (obligatorio)
    if (!formData.phone) {
      setError('El teléfono es obligatorio');
      setLoading(false);
      return;
    }
    
    if (!validatePhoneNumber(formData.phone, formData.country_code)) {
      const country = latinAmericaCountries.find(c => c.code === formData.country_code);
      setError(`El teléfono debe tener ${country?.phoneLength} dígitos para ${country?.name}`);
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.register({
        email: formData.email,
        full_name: formData.full_name,
        phone: formData.phone,
        country_code: formData.country_code,
        password: formData.password,
        router_count: formData.router_count
      });
      
      login(response.data.access_token, response.data.user, response.data.license);
      // 🆕 Redirigir directo al dashboard (NO al wizard)
      // Usuario ya tiene licencia FREE creada automáticamente
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-8 transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      {/* Register Form - Centered */}
      <div className="w-full max-w-md">
          <div className={`rounded-2xl shadow-xl p-8 transition-colors duration-300 ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <div className="text-center mb-8">
              <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Crear Cuenta</h2>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Comienza tu prueba gratuita de 30 días</p>
            </div>

            {error && (
              <div className={`mb-6 p-4 border rounded-lg text-sm ${
                isDark 
                  ? 'bg-red-900/20 border-red-800 text-red-300' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nombre Completo
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  onKeyDown={(e) => {
                    // Permitir explícitamente espacios y caracteres especiales
                    // No bloquear ninguna tecla
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark 
                      ? 'bg-white border-gray-600 text-gray-900 placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Juan Pérez García"
                  autoComplete="name"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Correo Electrónico
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark 
                      ? 'bg-white border-gray-600 text-gray-900 placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  País
                </label>
                <select
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value, phone: '' })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark 
                      ? 'bg-white border-gray-600 text-gray-900' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  {latinAmericaCountries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name} ({country.dialCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Teléfono
                </label>
                <div className="flex gap-2">
                  <div className={`flex items-center px-4 py-3 border rounded-lg font-semibold ${
                    isDark 
                      ? 'bg-gray-200 border-gray-600 text-gray-700' 
                      : 'bg-gray-100 border-gray-300 text-gray-700'
                  }`}>
                    {latinAmericaCountries.find(c => c.code === formData.country_code)?.dialCode}
                  </div>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      const country = latinAmericaCountries.find(c => c.code === formData.country_code);
                      if (digits.length <= (country?.phoneLength || 10)) {
                        setFormData({ ...formData, phone: digits });
                      }
                    }}
                    className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDark 
                        ? 'bg-white border-gray-600 text-gray-900 placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder={latinAmericaCountries.find(c => c.code === formData.country_code)?.phoneFormat.replace(/X/g, '0')}
                  />
                  />
                </div>
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Formato: {latinAmericaCountries.find(c => c.code === formData.country_code)?.phoneFormat}
                </p>
              </div>

              {/* Campo router_count oculto - ya no se usa en el nuevo sistema modular */}
              {/* <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ¿Cuántos routers deseas administrar?
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  required
                  value={formData.router_count}
                  onChange={(e) => setFormData({ ...formData, router_count: e.target.value === '' ? '' : parseInt(e.target.value) })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark 
                      ? 'bg-white border-gray-600 text-gray-900 placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="1"
                />
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cada router tendrá su propia conexión VPN</p>
              </div> */}

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Contraseña
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark 
                      ? 'bg-white border-gray-600 text-gray-900 placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark 
                      ? 'bg-white border-gray-600 text-gray-900 placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creando cuenta...' : 'Crear Cuenta Gratis'}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className={`font-semibold ${
                  isDark 
                    ? 'text-blue-400 hover:text-blue-300' 
                    : 'text-blue-600 hover:text-blue-700'
                }`}>
                  Inicia sesión
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Maintenance Mode Modal */}
        {showMaintenanceModal && maintenanceStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
              <div className="text-center">
                <div className="text-4xl mb-4">⚠️</div>
                <div className="text-gray-900 whitespace-pre-wrap text-lg leading-relaxed mb-6">
                  {maintenanceStatus.message.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < maintenanceStatus.message.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Volver al Login
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default RegisterPage;
